import CopyablePhone from '@/components/copyable-phone';
import { useOpenClient } from '@/hooks/use-open-client';
import VbivStatus from '@/components/vbiv-status';
import type { IUssd } from '@/entities/ussd.entity';
import { cn } from '@/lib/utils';
import { CalendarClockIcon, ContainerIcon, HashIcon, PhoneForwardedIcon } from 'lucide-react';
import { memo, useCallback, type MouseEvent } from 'react';
import { toast } from 'sonner';
import TimeAgo from 'react-timeago';
import russianStrings from 'react-timeago/lib/language-strings/ru';
import buildFormatter from 'react-timeago/lib/formatters/buildFormatter';

const formatter = buildFormatter(russianStrings);

const UssdCard = memo(function UssdCard({ ussd }: { ussd: IUssd }) {
	const { openClient } = useOpenClient();
	const goToClient = useCallback(() => {
		openClient(ussd.clientId, { listType: 'ussd', clientId: ussd.clientId, itemId: ussd.id });
	}, [openClient, ussd.clientId, ussd.id]);
	const title = ussd.operator || ussd.usedCommand || 'USSD';

	function handleCopy(e: MouseEvent, text: string) {
		e.stopPropagation();
		navigator.clipboard
			.writeText(text)
			.then(() => toast.success('Скопировано'))
			.catch(() => toast.error('Ошибка копирования'));
	}

	return (
		<div
			role='button'
			tabIndex={0}
			className='relative p-2 bg-card rounded-md flex flex-col gap-1.5 border-border border border-l-2 overflow-hidden cursor-pointer active:bg-accent/40'
			onClick={goToClient}
			onKeyDown={e => {
				if (e.key === 'Enter' || e.key === ' ') {
					e.preventDefault();
					goToClient();
				}
			}}
		>
			<div className='flex justify-between items-center'>
				<span className='flex gap-1 items-center min-w-0'>
					<div className='w-[18px] shrink-0'>
						<PhoneForwardedIcon size={16} className='text-chart-2 opacity-80' />
					</div>
					<button
						type='button'
						className='text-sm font-semibold text-chart-2 hover:bg-accent rounded-xs px-0.5 cursor-pointer select-none truncate'
						onClick={e => handleCopy(e, title)}
					>
						{title}
					</button>
				</span>
				<div onClick={e => e.stopPropagation()}>
					<VbivStatus clientId={ussd.clientId} takeStatus={ussd.takeStatus} takenFrom={ussd.takenFrom} />
				</div>
			</div>

			{ussd.comment ? (
				<span className='text-sm text-[#e89bff] truncate' onClick={e => handleCopy(e, ussd.comment || '')}>
					{ussd.comment}
				</span>
			) : null}

			<div className='flex justify-between'>
				<span className='flex gap-1 items-center break-all'>
					<div className='w-[18px]'>
						<HashIcon size={16} className='opacity-40' />
					</div>
					<span className='text-sm hover:bg-accent cursor-pointer select-none' onClick={e => handleCopy(e, '#' + ussd.clientId)}>
						{ussd.clientId}
					</span>
				</span>
			</div>

			<div className='flex justify-between'>
				<span className='flex gap-1 items-center'>
					<div className='w-[18px]'>
						<ContainerIcon size={16} className='opacity-40' />
					</div>
					<span className='text-sm hover:bg-accent cursor-pointer select-none' onClick={e => handleCopy(e, ussd.buildId)}>
						{ussd.buildId}
					</span>
				</span>
			</div>

			{ussd.phone ? (
				<div className='flex justify-between'>
					<CopyablePhone
						value={ussd.phone}
						onCopy={text => {
							navigator.clipboard
								.writeText(text)
								.then(() => toast.success('Скопировано'))
								.catch(() => toast.error('Ошибка копирования'));
						}}
						prefix='Номер: '
						className='text-sm opacity-80 truncate rounded-xs px-0.5'
					/>
				</div>
			) : null}

			{ussd.failureCode != null && ussd.failureCode !== 0 ? (
				<span className='text-sm text-destructive'>Код ошибки: {ussd.failureCode}</span>
			) : null}

			<button
				type='button'
				className={cn(
					'block w-full text-left rounded-2xl rounded-tl-sm bg-muted/50 px-2.5 py-2 text-sm leading-relaxed hover:bg-muted/70 cursor-pointer select-none',
				)}
				onClick={e => handleCopy(e, ussd.response || ussd.usedCommand || '')}
			>
				{ussd.response || ussd.usedCommand || '—'}
			</button>

			<span className='flex gap-1 items-center'>
				<div className='w-[18px]'>
					<CalendarClockIcon size={16} className='opacity-40' />
				</div>
				<span className='text-sm font-semibold opacity-40'>Получен</span>
				<div className='text-sm text-chart-2 opacity-95'>
					<TimeAgo date={ussd.createdAt} formatter={formatter} />
				</div>
			</span>
		</div>
	);
});

export default UssdCard;
